import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { SidebarDrawerContext } from "@/components/SidebarDrawerContext";

/**
 * Shared layout for sidebar-driven pages: a full-height collapsible sidebar on
 * the left, with the top navigation, page content, and footer stacked in the
 * right column. Pass the role-specific sidebar via `sidebar`.
 *
 * Below `lg` the sidebar collapses out of the flow and reappears as an
 * off-canvas drawer; the state lives here because both the opener (the
 * hamburger inside Navigation) and the drawer itself (inside `sidebar`) are
 * descendants.
 */
export default function AppShell({ sidebar, children }: { sidebar: ReactNode; children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  // Navigating from inside the drawer should leave it behind. Adjusted during
  // render rather than in an effect (react-hooks/set-state-in-effect): React
  // re-runs this component immediately with the new state, so the stale open
  // drawer is never committed — no flash, no cascading render.
  const [drawerPath, setDrawerPath] = useState(pathname);
  if (drawerPath !== pathname) {
    setDrawerPath(pathname);
    setDrawerOpen(false);
  }

  // Don't let the page scroll underneath the open drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  // Escape closes it, the way every other overlay in the app behaves.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const close = useCallback(() => setDrawerOpen(false), []);
  const toggle = useCallback(() => setDrawerOpen((v) => !v), []);
  const drawer = useMemo(() => ({ open: drawerOpen, toggle, close }), [drawerOpen, toggle, close]);

  return (
    <SidebarDrawerContext.Provider value={drawer}>
      <div className="flex min-h-screen">
        {sidebar}
        <div className="flex min-w-0 flex-1 flex-col">
          <Navigation showBrand={false} showSettings={false} showBorder={false} />
          <main className="min-w-0 flex-1">{children}</main>
          <Footer />
        </div>
      </div>
    </SidebarDrawerContext.Provider>
  );
}
